import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, X } from "lucide-react";
import { fetchLaunchStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "rigcalc.deal_banner.dismissed_on";
const today = () => new Date().toDateString();

function Box({ n, label }) {
  return (
    <div className="flex flex-col items-center leading-none min-w-[22px]">
      <span className="mono text-sm md:text-base font-bold tabular-nums">{String(n).padStart(2, "0")}</span>
      <span className="mono text-[8px] tracking-widest opacity-70">{label}</span>
    </div>
  );
}

function Countdown({ end }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(new Date(end).getTime() - now, 0);
  const parts = [
    [Math.floor(diff / 86400000), "D"],
    [Math.floor((diff % 86400000) / 3600000), "H"],
    [Math.floor((diff % 3600000) / 60000), "M"],
    [Math.floor((diff % 60000) / 1000), "S"],
  ];
  return (
    <div data-testid="deal-countdown" className="flex items-center gap-1.5">
      {parts.map(([n, l], i) => (
        <span key={l} className="flex items-center gap-1.5">
          {i > 0 && <span className="mono font-bold opacity-60">:</span>}
          <Box n={n} label={l} />
        </span>
      ))}
    </div>
  );
}

export function DealBanner() {
  const { user, isPaid } = useAuth();
  const [sale, setSale] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem(DISMISS_KEY) === today());

  useEffect(() => {
    if (hidden) return;
    fetchLaunchStatus().then((s) => setSale(s.pro_sale)).catch(() => {});
  }, [hidden]);

  if (hidden || user === null || isPaid || !sale?.active || new Date(sale.ends_at) <= new Date()) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, today());
    setHidden(true);
  };

  return (
    <div data-testid="deal-banner" className="relative bg-[#0b0e13] border-b-2 border-[#f59e0b] text-[#f8fafc] px-10 py-2 flex flex-wrap items-center justify-center gap-3 md:gap-5">
      <span className="flex items-center gap-2 mono text-[10px] md:text-xs tracking-widest font-bold uppercase text-[#f59e0b]">
        <Flame className="w-4 h-4" /> November: Pro ${sale.sale_price}
      </span>
      <span className="hidden md:inline text-xs text-[#94a3b8]">First month · ends in</span>
      <Countdown end={sale.ends_at} />
      <Link
        to="/pricing"
        data-testid="deal-banner-cta"
        className="mono text-[10px] md:text-xs tracking-widest font-bold uppercase bg-[#f59e0b] text-[#0b0e13] px-3 py-1.5 hover:bg-[#fbbf24] transition-colors"
      >
        Get the deal →
      </Link>
      <button
        data-testid="deal-banner-dismiss"
        onClick={dismiss}
        aria-label="Hide deal banner for today"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#f8fafc] transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
